/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable complexity */
/* global window */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { ISearchType, SearchMapRoute, SearchRoute, useRouter } from '@search/offer-search-routes/src';

import { RegionIdEnum } from '@search/filter-enums/enums/Region';
import { CurrencyEnum } from '@search/graphql-typings';
import { AuthorTypeOptionsWithRealtor, DealType, NewBuilding } from '@search/filter-enums/enums';
import { useM2AuthContext } from '@search/auth/src/M2AuthProvider';
import { useGeoRootRegion } from '@search/geo/src/GeoRootRegionProvider';
import { isBatchItem } from '@search/geo/src/isBatchItem';
import { useMarketingSend } from '@search/classified-common/src/marketing/useMarketingSend';
import { SortEnum } from '@search/filter-enums/mappings/sort_label_mapping';
import { useMinimalMortgageRateLoader } from '@search/vtbeco-frontend-core/view/common/components/MinimalBanksMortgage/MinimalMortgageRate/useMinimalMortgageRate';
import { seoSearch } from '../../../seo/seoSearch';
import { useQueryDisplayValues } from '../../../../view/common/hooks/useQueryDisplayValues';
import { useLastSearches } from '../../../../src/services/useLastSearches/useLastSearches';

import {
    AuthorFilter,
    DealTypeFilter,
    Filters,
    GeoFilter,
    MapBoundsFilter,
    PriceFilter,
    RegionFilter,
    NewBuildingFilter
} from '../../../../view/filters/models/Filter';
import { classifiedFilterFactory } from '../../filters/ClassifiedFilterFactory';
import { ClassifiedFilterCollection } from '../../filters/ClassifiedFilterCollection';
import { getSortValues } from '../../filters/getSortValues';
import { GeoBaseStore } from '../../../geo/GeoBase';
import { useClassifiedOfferDataLayerPush } from '../../analytics/ClassifiedOfferGtmContext';
import { useOfferSearchBreadcrumbSections } from '../breadcrumbSections';
import { offerRegionToMortgage } from '../../regionToBackoffice';
import { offerFilterGqlFromRoute, offerGqlSortFromRoute } from './offerFilterGqlFromRoute';
import { offerSearchPageFilterToRoute } from './offerSearchPageFilterToRoute';
import { offerSearchPageGetInitialFilters } from './offerSearchPageGetInitialFilters';
import { useSearchOffersLoader } from './SearchOffersQuery';
import {
    SearchOffersQuery$data
} from './SearchOffersQuery/__generated__/SearchOffersQuery.graphql';
const prev = new WeakSet();

// eslint-disable-next-line complexity
export function useOfferSearchPageState2(
    searchRoute: SearchRoute | SearchMapRoute,
    // eslint-disable-next-line react-hooks/rules-of-hooks
    loadOffers = useSearchOffersLoader(),
    isMobile: boolean = false
) {
    const searchParams = searchRoute.params();
    const sortValues = React.useMemo(
        () => getSortValues({
            realtyType: searchParams.categoryType,
            dealType: searchParams.dealType,
            regionId: searchParams.region,
            sort: searchParams.sort
        }),
        [ searchParams.categoryType, searchParams.dealType, searchParams.region, searchParams.sort ]
    );

    const router = useRouter();
    const historyState = React.useMemo(
        () => router.selector<{ resp: SearchOffersQuery$data; pageNumber: number }>('useOfferSearchPageState2'),
        [ router ]
    );

    const hs = historyState.get();

    let pageNumber = searchParams.pageNumber === 1 ? undefined : searchParams.pageNumber;

    pageNumber = pageNumber ?? hs?.pageNumber ?? 1;

    const initialFilters = React.useMemo(
        () => offerSearchPageGetInitialFilters(searchParams),
        [ searchParams ]
    );

    const [ state, updateState ] = React.useState({
        filters: initialFilters,
        sort: searchParams.sort,
        pageNumber,
        addMorePageNumber: undefined as number | undefined,
        pageSize: Math.min(20, searchParams.pageSize ?? 10)
    });

    const [ mortgagePaymentSearchInfoModal, setMortgagePaymentSearchInfoModal ] = useState(false);
    const [ sendMortgageModal, setSendMortgageModal ] = useState(false);

    const openMortgagePaymentSearchModal = useCallback(() => {
        setMortgagePaymentSearchInfoModal(true);
    }, [ ]);
    const closePaymentInfoModal = useCallback((sendMortgage: boolean) => {
        setMortgagePaymentSearchInfoModal(false);
        if (sendMortgage) setSendMortgageModal(true);
    }, [ ]);
    const closeSendMortgageModal = useCallback(() => {
        setSendMortgageModal(false);
    }, [ ]);

    // for working history back
    React.useEffect(() => {
        updateState(prev => ({
            ...prev,
            filters: initialFilters
        }));
    }, [ initialFilters ]);

    const { filters } = state;

    const clearCache = React.useCallback((refresh = false) => {
        historyState.clear();
        // loadOffers.clearCache(refresh);
        // eslint-disable-next-line no-undef
        window.scrollTo(0, 0);
    }, [ loadOffers, historyState ]);

    const updateStateAndRoute = React.useCallback((next: Partial<typeof state>) => {
        historyState.clear();
        // eslint-disable-next-line no-undef
        window.scrollTo(0, 0);
        updateState(prev => {
            return { ...prev, pageNumber: 1, addMorePageNumber: undefined, ...next };
        });

        const prev = searchRoute.params();
        // eslint-disable-next-line no-shadow
        const sort = next.sort ?? prev.sort;
        const newParams = {
            ...(next.filters ? offerSearchPageFilterToRoute(next.filters) : prev as any),
            pageNumber: next.pageNumber ?? prev.pageNumber,
            addMorePageNumber: undefined,
            pageSize: next.pageSize ?? prev.pageSize,
            sort: sort === 'unknown' ? undefined : sort
        };

        searchRoute.push(newParams, { delay: 1000 });
    }, [ searchRoute, historyState ]);

    const handlePageChange = React.useCallback((next: number) => {
        updateStateAndRoute({ pageNumber: next });
    }, [ updateStateAndRoute ]);

    // eslint-disable-next-line no-shadow
    const handleChangeSort = React.useCallback((sort: ISearchType['sort'] | ISearchType['sort'][]) => {
        if (Array.isArray(sort)) sort = sort[0] ?? 'unknown';
        updateStateAndRoute({ sort, pageNumber: 1 });
    }, [ updateStateAndRoute ]);

    const handleClickOnBreadcrumb = React.useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>, url: string) => {
            if (url === '/') return;
            e.preventDefault();
            router.update(url);
        },
        [ router ]
    );

    const [ isFiltersChanged, setIsFiltersChanged ] = useState(false);

    // eslint-disable-next-line no-shadow
    const updateFilters = React.useCallback((filters: ClassifiedFilterCollection) => {
        const nextDealType = (filters.get(Filters.dealType) as DealTypeFilter).dealType;
        const nextRegion = (filters.get(Filters.region) as RegionFilter).region;
        const curr = searchRoute.params();

        let nextSort: undefined | 'unknown';

        if (
            (
                curr.sort === SortEnum.TIME_TO_METRO ||
                curr.sort === SortEnum.DISTANCE_TO_CITY_CENTER ||
                (nextDealType === DealType.RENT && searchParams.dealType === DealType.SELL)
            ) &&
            ! getSortValues(searchParams.categoryType, nextDealType, nextRegion).find(r => r.value === curr.sort)) {
            nextSort = 'unknown';
        }

        setIsFiltersChanged(true);
        updateStateAndRoute({
            filters,
            pageNumber: 1,
            addMorePageNumber: undefined,
            ...(nextSort && { sort: nextSort })
        });
    }, [ updateStateAndRoute ]);

    const handleReset = React.useCallback(() => {
        const initFilters = filters.reset();
        const nextFilters = classifiedFilterFactory.createOrUpdateFilters(initFilters.getFilters())
            .update(new MapBoundsFilter()) as ClassifiedFilterCollection;

        updateFilters(nextFilters);
    }, [ updateFilters, filters ]);

    const handleResetInSegment = React.useCallback(() => {
        filters.remove(filters.get(Filters.price));
        filters.remove(filters.get(Filters.rooms));
        filters.remove(filters.get(Filters.newBuilding));
        const newFilters = classifiedFilterFactory.clearAdditionalFilters(filters.getFilters());
        const nextFilters = classifiedFilterFactory.createOrUpdateFilters(newFilters)
            .update(new MapBoundsFilter()) as ClassifiedFilterCollection;

        updateFilters(nextFilters);
    }, [ updateFilters, filters ]);

    const regionId = searchParams.region;

    const nbId = searchParams.newBuildingIds[0] ?? 0;
    const auth = useM2AuthContext();
    const isMyOffers = auth.auth.isAuthenticated && auth.auth.isRealtorEmployeeEmpowered;
    const userId = isMyOffers ? auth.auth.user?.userId : null;
    const filtersParams = offerFilterGqlFromRoute({ ...searchParams, userId });

    const onSwitchToNb = React.useCallback(() => {
        const newFilters = classifiedFilterFactory
            .createOrUpdateFilters(filters.update(new NewBuildingFilter([ NewBuilding.NEW ])).getFilters());

        updateFilters(newFilters);
    });

    const sort = offerGqlSortFromRoute(searchParams.sort);
    const skipSeoCloudBack = ! router.currentUrl.includes('dbg_back_cloud_enabled=1');
    const isSellFlat = useMemo(() => filters.isSellFilterActive() && filters.isFlatFilterActive(), [ filters ]);
    const pageNumberParam = isMobile ? state.pageNumber : (state.addMorePageNumber ?? state.pageNumber);
    const metroBranch = searchParams.metroBranch ? [ searchParams.metroBranch ] : null;

    const params = {
        realtyObject: filtersParams.realtyObject,
        filters: filtersParams,
        regionId,
        nbId,
        geos: [
            ...searchParams.districtIds,
            ...searchParams.addresses,
            ...searchParams.geoSlugs ?? [],
            ...searchParams.metroIds ?? [],
            ...metroBranch ?? []
        ],
        skipSeoCloudBack,
        paging: {
            pageNumber: pageNumberParam,
            pageSize: state.pageSize
        },
        sort: [ sort ],
        pathName: router.location.pathname,
        limit: 1,
        onlyUnique: true,
        // isFiltersChanged здесь нельзя использовать, т.к.
        // 1. isFiltersChanged - часть ключа кэша, по которому loader вытаскивает данные
        // 2. при смене фильтров isFiltersChanged меняется сразу, ключ меняется и loader начинает грузить данные
        // 3. спустя секунду меняется searchParams, снова меняется ключ и loader посылает второй запрос
        // 4. создается гонка и второй ответ может быть перезатерт первым, что приводит к баге https://jira.m2.ru/browse/RS-6522
        skipLastSearch: ! isSellFlat || (isMobile && pageNumberParam > 1)
    };

    const response = loadOffers(params);
    const currentRef = React.useRef<ReturnType<typeof loadOffers>['data']>();

    if (response.data) currentRef.current = response.data;
    const current = currentRef.current;

    const resolveRef = React.useRef(() => {});
    const stateRef = React.useRef(state);

    stateRef.current = state;

    // const pageNumberRef = React.useRef(state.pageNumber);

    // pageNumberRef.current = state.pageNumber;

    const rootRegions = useGeoRootRegion();

    const filtersRef = React.useRef(filters);

    filtersRef.current = filters;

    const data = React.useMemo(() => {
        const prev = historyState.get()?.resp;

        if (! current) return prev;

        const prevItems = prev?.searchOffers.items ?? [];
        const currentItems = current?.searchOffers?.items ?? [];
        const isEqualLastPart = currentItems.map(offer => offer.id).join('_') ===
            prevItems.slice(-currentItems.length).map(offer => offer.id).join('_');

        const prevNbOffers = prev?.searchOffers.similarNb ?? [];
        const currentNbOffers = current?.searchOffers.similarNb ?? [];
        const isEqualLastPartNb = currentNbOffers.map(offer => offer.id).join('_') ===
            prevNbOffers.slice(-currentNbOffers.length).map(offer => offer.id).join('_');

        const items = isEqualLastPart ? prevItems : [ ...prevItems, ...currentItems ];
        const similarNb = isEqualLastPartNb ? prevNbOffers : [ ...prevNbOffers, ...currentNbOffers ];
        const geoIdSet = new Set(current.geo2.items.filter(isBatchItem).map(geo => geo.id));
        const next: SearchOffersQuery$data = {
            ...current,
            searchOffers: {
                seoTags: current?.searchOffers.seoTags,
                paging: current.searchOffers?.paging ?? null,
                emptyResultTitle: current.searchOffers?.emptyResultTitle,
                items: items.map(item => ({
                    ...item,
                    location: {
                        ...item.location,
                        clickableAddress: item.location.clickableAddress?.map(addr => ({
                            ...addr,
                            isInRoute: geoIdSet.has(addr.id) ?? false
                        })),
                    },
                })),
                similarNb
            }
        };

        const geoFilter = filtersRef.current.get(Filters.geo) as GeoFilter;

        current.geo2.items?.forEach(geo => {
            if (geo.__typename === 'GeoObject') geoFilter.add(geo);
        });

        historyState.set({ resp: next, pageNumber: stateRef.current.pageNumber });

        resolveRef.current();

        return next;
    }, [ current, historyState ]);

    const stopIndexRef = React.useRef(0);

    stopIndexRef.current = state.pageNumber * state.pageSize;

    const isRowLoaded = React.useCallback(({ index }: { index: number}) => {
        return stopIndexRef.current >= index;
    }, [ ]);

    const handleShowMore = React.useCallback((
        {
            startIndex, stopIndex
        }: {
            startIndex?: number;
            stopIndex?: number;
        } = {}
    ) => {
        updateState(prev => {
            const prevPage = prev.addMorePageNumber ?? prev.pageNumber;
            const nextPage = stopIndex ? Math.max(Math.ceil(stopIndex / state.pageSize), prevPage) : undefined;

            return {
                ...prev,
                pageNumber: nextPage ?? (prevPage + 1),
                addMorePageNumber: nextPage ?? (prevPage + 1)
            };
        });

        return new Promise<void>(resolve => {
            resolveRef.current = resolve;
        });
    }, [ state.pageSize ]);

    const geoStore = React.useMemo(
        () => GeoBaseStore.fromGql({
            ...data?.geo ?? {},
            districts: [
                ...data?.geo?.districts ?? [],
                ...data?.geo2.items.filter(isBatchItem) ?? []
            ]
        }),
        [ data?.geo, data?.geo2.items ]
    );

    geoStore.districts.regionId = searchParams.region;

    const collection = data?.searchOffers?.items ?? [];
    const similarNbCollection = data?.searchOffers?.similarNb ?? [];
    const totalItems = data?.searchOffers?.paging?.total ?? 0;
    const emptyResultTitle = data?.searchOffers?.emptyResultTitle;

    const isNeedPagination = collection.length !== 0 && totalItems > state.pageSize && ! emptyResultTitle;

    const totalPages = Math.ceil(totalItems / (Math.max(1, state.pageSize)));
    const isLastPageNumber = Math.max(1, state.pageNumber) >= totalPages ||
        collection.length === 0;

    const dataLayerPush = useClassifiedOfferDataLayerPush();
    const offersCount = data?.searchOffersSummary?.count ?? 0;

    React.useEffect(() => {
        if (offersCount === 0) dataLayerPush({ event: 'classified_serp_empty_search_output' });
    }, [ offersCount ]);

    const rootRegion = rootRegions.byId(searchParams.region);

    const [ queryDisplayValues, addQueryDisplayValueRaw ] = useQueryDisplayValues({
        newBuildingIds: searchParams.newBuildingIds,
        // addressIds: searchParams.addresses
    });

    const addQueryDisplayValue: typeof addQueryDisplayValueRaw = React.useCallback((key, value) => {
        rootRegions.slugs.add({
            ...value,
            kind: value.type
        }, regionId);

        addQueryDisplayValueRaw(key, value);
    }, [ rootRegions, regionId, addQueryDisplayValueRaw ]);

    const geos = current?.geo2.items.filter(isBatchItem) ?? [];

    const { pageTitle: headerTitle, faqTexts, metaTitle } = seoSearch({
        params: searchParams,
        geos,
        region: rootRegion,
        offersCount,
        offersSummary: data?.searchOffersSummary
    });

    useEffect(() => {
        if (window) window.document.title = data?.searchOffersSeo?.metaTitle ?? metaTitle;
    }, [ metaTitle, data?.searchOffersSeo?.metaTitle ]);

    const marketingSend = useMarketingSend();

    useEffect(() => {
        if (data?.searchOffers.paging?.pageNumber === 1) {
            marketingSend({
                type: 'search',
                newBuilding: searchParams.newBuilding,
                regionId: searchParams.region,
                offerIds: emptyResultTitle ? [] : collection.map(({ id }) => id)
            });
        }
    }, [ collection, data?.searchOffers.paging?.pageNumber ]);

    const priceFilter = filters.get(Filters.price) as PriceFilter;
    const estimatePrice = priceFilter?.value.to ? {
        value: priceFilter.value.to * 100, // копейках, центах
        currency: CurrencyEnum.Rub
    } : undefined;

    const showMortgageBanner = (
        filters.isSellFilterActive() &&
        (
            filters.isFlatFilterActive() ||
            filters.isRoomFilterActive() ||
            filters.isActiveFilterHouseAllowedMortgage() ||
            filters.isLandFilterActive()
        ) &&
        (priceFilter.value.from ?? 0) <= 50e6
    );

    const isRealtorAuthorFilterActive = (
        filters.get(Filters.author) as AuthorFilter
    ).author.includes(AuthorTypeOptionsWithRealtor.REALTOR);

    const breadcrumbSections = useOfferSearchBreadcrumbSections({
        ...searchParams,
        geos,
        rootRegion,
        offersCount
    });

    const mortgagePurpose = filters.getMortgagePurpose();

    const isMskMOSPbLO = useMemo(() => [
        RegionIdEnum.MSK,
        RegionIdEnum.MSK_OBL,
        RegionIdEnum.MSK_AND_MSK_OBL,
        RegionIdEnum.SPB,
        RegionIdEnum.LEN_OBL,
        RegionIdEnum.SPB_AND_LEN_OBL
    ].includes(regionId), [ regionId ]);

    const [ isQuizModalOpen, toggleQuizModal ] = useState(false);
    const openQuizModal = useCallback(() => toggleQuizModal(true), []);
    const closeQuizModal = useCallback(() => toggleQuizModal(false), []);
    const showNbQuiz = isMskMOSPbLO && filters.isNewBuilding();
    const showPromoRieltors = isMskMOSPbLO && ! filters.isNewBuilding() && isSellFlat;

    const isNeedlessSave = offersCount === 0 ||
        ! filters.getCountActiveFilters() ||
        // in mobile proactive loading
        state.pageNumber > (isMobile ? 2 : 1);
    const { lastSearches } = useLastSearches({
        lastSearches: data?.getLastSearches?.lastSearches,
        filtersParams,
        isNeedlessSave,
        isShowedInSegment: isSellFlat,
        isFiltersChanged,
        currentUrl: router.currentUrl,
        isMobile
    });

    const loadRate = useMinimalMortgageRateLoader();

    const { rate: secondaryMortgageRate } = loadRate({
        regionId: offerRegionToMortgage(regionId),
        isNewBuilding: false,
        propertyType: filtersParams.realtyObject,
        avgPricePerUnit: data?.searchOffersSummary?.avgPricePerUnit
    });

    const { rate: primaryMortgageRate } = loadRate({
        regionId: offerRegionToMortgage(regionId),
        isNewBuilding: true,
        propertyType: filtersParams.realtyObject,
    });

    const minimalMortgageRate = {
        secondaryMortgageRate,
        primaryMortgageRate
    };

    return {
        ...state,
        similarNbCollection,
        summary: data?.searchOffersSummary,
        seoTags: data?.searchOffers.seoTags,
        pageNumber: state.pageNumber,
        pageSize: state.pageSize,
        headerTitle: data?.searchOffersSeo?.h1 ?? headerTitle,
        faqTexts,
        collection,
        emptyResultTitle,
        mortgageBannerMinutesVariant: data?.mortgageBannerMinutesVariant.value ?? 0,
        mortgageBannerTextVariant: data?.mortgageBannerTextVariant.value ?? 0,
        pagination: {
            totalItems,
            isLastPageNumber
        },
        clearCache,
        newBuilding: data?.newBuilding,
        count: offersCount,
        loading: response?.loading ?? false,
        geoStore,
        rootRegion,
        sortValues,
        isNeedPagination,
        queryDisplayValues,
        hasOfferWithoutVas: data?.userDetails?.hasOfferWithoutVas ?? false,
        userId: data?.userDetails?.userId ?? '',
        userPhone: data?.userDetails?.phone ?? '',
        estimatePrice,
        showMortgageBanner,
        isRealtorAuthorFilterActive,
        breadcrumbSections,
        mortgagePurpose,
        isQuizModalOpen,
        showNbQuiz,
        showPromoRieltors,
        lastSearches,
        minimalMortgageRate,

        handleReset,
        handleResetInSegment,
        handleChangeSort,
        handlePageChange,
        handleShowMore,
        isRowLoaded,
        updateFilters,
        addQueryDisplayValue,
        handleClickOnBreadcrumb,
        onSwitchToNb,
        openQuizModal,
        closeQuizModal,
        isFiltersChanged,
        mortgagePaymentSearchInfoModal,
        sendMortgageModal,
        closePaymentInfoModal,
        closeSendMortgageModal,
        openMortgagePaymentSearchModal
    };
}

export function useOfferSearchMapPageState(
    selected: { geoHash?: string; selectedOffers?: readonly string[] }
) {
    const router = useRouter();
    const searchRoute = router.route(SearchMapRoute);
    const searchParams = searchRoute.params();
    const nbId = searchParams.newBuildingIds[0] ?? 0;

    const load = useSearchOffersLoader();

    const auth = useM2AuthContext();
    const isMyOffers = auth.auth.isAuthenticated && auth.auth.isRealtorEmployeeEmpowered;
    const userId = isMyOffers ? auth.auth.user?.userId : null;
    const gqlFilters = offerFilterGqlFromRoute({
        ...searchParams,
        selectedOffers: selected.geoHash === undefined ? selected.selectedOffers : undefined,
        mapBounds: {
            geohash: selected.geoHash
        },
        userId
    });

    const offersResponse = load({
        filters: gqlFilters,
        regionId: searchParams.region,
        realtyObject: gqlFilters.realtyObject,
        nbId,
        paging: {
            pageNumber: 1,
            pageSize: 30
        },
        pathName: router.location.pathname,
        limit: 1,
        onlyUnique: true,
        skipLastSearch: true,
        skipSeoCloudBack: true
    });

    return {
        newBuilding: offersResponse.data?.newBuilding,
        offersCount: offersResponse.data?.searchOffersSummary?.count,
        offersCountRealtyFormatted: offersResponse.data?.searchOffersSummary?.formattedRealty ?? '',
        loading: offersResponse.loading,
        collection: offersResponse.data?.searchOffers.items ?? []
    };
}

export function getParentRegion(regionId: RegionIdEnum) {
    let parentRegion = regionId ?? RegionIdEnum.MSK_AND_MSK_OBL;

    if (parentRegion === RegionIdEnum.MSK_OBL) parentRegion = RegionIdEnum.MSK_AND_MSK_OBL;
    if (parentRegion === RegionIdEnum.LEN_OBL) parentRegion = RegionIdEnum.SPB_AND_LEN_OBL;

    return parentRegion;
}
